import AddIcon from "@mui/icons-material/AddRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import { Button, IconButton, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import RoundedTable from "@entrilia/shared/components/RoundedTable";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import type { ObjectSelectConfiguration } from "../../../../../../api/types/workflowTypes";
import FieldEdit from "../../../../../entityFields/editors/FieldEdit";
import type { ChangeSetEditState, FieldState, RelatedChangeSetEditGroup } from "./changeSetEditState";
import {
  addRelatedChangeSetRowAction,
  removeRelatedChangeSetRowAction,
  updateRelatedChangeSetFieldsAction,
} from "./changeSetEditState";
import ObjectAutocomplete from "./ObjectAutocomplete";
import { buildObjectFieldUpdates, getObjectSelectValue, loadObjectSelectOptions } from "./objectSelectOptionsHelper";
import TransactionTableCell from "./TransactionTableCell";

interface Props {
  group: RelatedChangeSetEditGroup;
  setEditState: Dispatch<SetStateAction<ChangeSetEditState>>;
}

const noOutlineSx = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

const getObjectSelectConfiguration = (
  group: RelatedChangeSetEditGroup,
  fieldName: string
): ObjectSelectConfiguration | undefined =>
  group.configuration.objectSelectConfigurations.find((configuration) => configuration.fieldName === fieldName);

const getVisibleFields = (group: RelatedChangeSetEditGroup, fields: FieldState[]): FieldState[] => {
  const hiddenFieldNames = new Set(group.configuration.hiddenFieldNames);
  return fields.filter((field) => !hiddenFieldNames.has(field.name));
};

const RelatedChangeSetsEditTable = ({ group, setEditState }: Props) => {
  const { objectSelectConfigurations } = group.configuration;

  const loadOptions = useCallback(
    () => loadObjectSelectOptions(objectSelectConfigurations, adminApi.searchObjects),
    [objectSelectConfigurations]
  );

  const [objectSelectOptionsResp, fetchError, { isFetching }] = useFetch(loadOptions, {
    disabled: objectSelectConfigurations.length === 0,
  });

  const handleFieldChange = (changeSetId: string, fieldName: string, value: unknown) => {
    setEditState(updateRelatedChangeSetFieldsAction(group.name, changeSetId, { [fieldName]: value }));
  };

  const handleObjectChange = (
    changeSetId: string,
    configuration: ObjectSelectConfiguration,
    selectedObject: ObjectFieldValues | null
  ) => {
    setEditState(
      updateRelatedChangeSetFieldsAction(
        group.name,
        changeSetId,
        buildObjectFieldUpdates(configuration, selectedObject)
      )
    );
  };

  const handleAdd = () => {
    setEditState(addRelatedChangeSetRowAction(group.name));
  };

  const columnFields = group.rows[0] ? getVisibleFields(group, group.rows[0].fields) : [];
  const canAddRows = group.newChangeSetTemplate !== undefined;

  if (fetchError) {
    logError(fetchError, "[RelatedChangeSetsEditTable] loadObjectSelectOptions");
    return <DataLoadingFailed title="Failed to fetch required entities" />;
  }

  if (isFetching) {
    return <InlineLoader />;
  }

  const objectSelectOptions = objectSelectOptionsResp ?? {};

  return (
    <>
      {group.rows.length === 0 && (
        <Typography color="textSecondary" sx={{ textAlign: "center", mt: 1, mb: 3 }}>
          No rows
        </Typography>
      )}
      {group.rows.length > 0 && (
        <RoundedTable noPaddingInBodyCells>
          <TableHead>
            <TableRow>
              {columnFields.map((field) => (
                <TableCell key={field.name}>
                  {field.caption}
                  {field.attributes?.includes("Required") && <RequiredFieldMarker />}
                </TableCell>
              ))}
              <TableCell width={32} />
            </TableRow>
          </TableHead>
          <TableBody>
            {group.rows.map((row) => (
              <TableRow key={row.changeSetId}>
                {getVisibleFields(group, row.fields).map((field) => {
                  const objectSelectConfiguration = getObjectSelectConfiguration(group, field.name);

                  return (
                    <TransactionTableCell key={field.name} hasError={field.hasError}>
                      {objectSelectConfiguration ? (
                        <ObjectAutocomplete
                          options={objectSelectOptions[objectSelectConfiguration.fieldName] ?? []}
                          value={getObjectSelectValue(field.newValue, objectSelectConfiguration, objectSelectOptions)}
                          onChange={(value) => handleObjectChange(row.changeSetId, objectSelectConfiguration, value)}
                          sx={noOutlineSx}
                        />
                      ) : (
                        <FieldEdit
                          value={field.newValue}
                          fieldName={field.name}
                          fieldType={field.fieldType}
                          fieldConfiguration={field.fieldConfiguration}
                          fieldAttributes={field.attributes}
                          onChange={(fieldName, value) => handleFieldChange(row.changeSetId, fieldName, value)}
                          hasError={field.hasError}
                          sx={noOutlineSx}
                        />
                      )}
                    </TransactionTableCell>
                  );
                })}

                <TransactionTableCell width={60}>
                  <IconButton
                    color="error"
                    onClick={() => setEditState(removeRelatedChangeSetRowAction(group.name, row.changeSetId))}
                  >
                    <CloseIcon />
                  </IconButton>
                </TransactionTableCell>
              </TableRow>
            ))}
          </TableBody>
        </RoundedTable>
      )}
      <Button variant="text" startIcon={<AddIcon />} onClick={handleAdd} disabled={!canAddRows} sx={{ mt: 1 }}>
        Add New
      </Button>
    </>
  );
};

export default RelatedChangeSetsEditTable;
